import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES as IDS } from "../constants/testIds";
import { SERVICES, SUBSTRATES, CAPABILITIES } from "../lib/site-data";

const PageHeader = () => (
  <section className="bg-[color:var(--brand-navy)] text-white relative overflow-hidden">
    <div className="absolute inset-0 grid-noise opacity-20" />
    <div className="container-tg relative py-24 lg:py-32">
      <div className="overline text-[color:var(--brand-yellow)] mb-6">Services</div>
      <h1 className="font-display text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none max-w-4xl">
        Designed, engineered & built — <span className="text-[color:var(--brand-yellow)]">in house</span>.
      </h1>
      <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
        From the first sketch to the final finish, every Tecnografic service
        runs through our Fort Lauderdale facility — giving you tight quality
        control and the fastest manufacturing timeframe in the industry.
      </p>
    </div>
  </section>
);

const ServiceRow = ({ s, idx }) => {
  const reverse = idx % 2 === 1;
  return (
    <section data-testid={IDS.card(s.slug)} className="border-b border-[color:var(--surface-border)]">
      <div className="container-tg py-20 lg:py-28">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:flex-row-reverse" : ""}`}>
          <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
            <div className="relative">
              <div className={`absolute -z-10 w-40 h-40 bg-[color:var(--brand-yellow)] ${reverse ? "-left-6 -bottom-6" : "-right-6 -top-6"}`} />
              <div className="relative aspect-[4/3] overflow-hidden bg-white border border-[color:var(--surface-border)]">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
          <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
            <div className="overline mb-4">
              {String(idx + 1).padStart(2, "0")} · Service
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter mb-6">
              {s.title}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">{s.short}</p>
            <ul className="space-y-3 mb-10">
              {s.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 bg-[color:var(--brand-yellow)] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[color:var(--brand-navy)]" strokeWidth={3} />
                  </span>
                  <span className="text-slate-700">{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-primary text-xs">
              Discuss this service <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const MaterialsBlock = () => (
  <section className="bg-white border-b border-[color:var(--surface-border)]">
    <div className="container-tg py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="overline mb-4">Substrates</div>
          <h2 className="font-display text-4xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter">
            The right material, every time.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            We stock and source from the largest plastic and metal distributors
            in the US — and advise on weight, climate and aesthetic for every
            project.
          </p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[color:var(--surface-border)] border border-[color:var(--surface-border)]">
          {SUBSTRATES.map((m) => (
            <div key={m} className="bg-white p-6 text-center">
              <span className="font-display font-bold text-[color:var(--brand-navy)] text-lg">{m}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CapabilitiesGrid = () => (
  <section className="container-tg py-24">
    <div className="overline mb-4">Workshop</div>
    <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter mb-12 max-w-3xl">
      The capability stack behind every part.
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {CAPABILITIES.map((c) => (
        <div key={c.title} className="card-tg">
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)] mb-2">
            {c.title}
          </h3>
          <p className="text-sm text-slate-600">{c.detail}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader />
      {SERVICES.map((s, i) => (
        <ServiceRow key={s.slug} s={s} idx={i} />
      ))}
      <MaterialsBlock />
      <CapabilitiesGrid />
    </div>
  );
}
